import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { promptApi } from '../services/api';
import { useCompany } from '../contexts/CompanyContext';
import TagPill from './ui/TagPill';

interface Prompt {
  id: string;
  name: string;
  description?: string;
  type: string;
  /** bd-deepcontext-qiwz7 — comma-separated lowercase ISO2 country codes
   *  (e.g. "lt,es,us"), often null. Present on the full blueprint object the
   *  selected-prompt cache fetches via promptApi.getById. */
  countries?: string | null;
}

/** Blueprint-usage map (bd-n3yqb). Keyed by promptId; each entry names the
 *  scheduled job that already owns that blueprint. */
export type BlueprintUsageMap = Record<string, { jobId: string; jobName: string }>;

interface PromptMultiSelectProps {
  value: string[];
  onChange: (promptIds: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** Optional: Pass prompts from parent to avoid duplicate API calls */
  externalPrompts?: Prompt[];
  /**
   * bd-n3yqb — map of blueprints already used by a scheduled job. Blueprints
   * owned by ANOTHER job are disabled in the dropdown and show the owning job
   * name. The job currently being edited is excluded via `ownJobId` so its own
   * blueprints stay selectable.
   */
  blueprintUsage?: BlueprintUsageMap;
  /** Id of the job being edited; usage entries pointing at this job are ignored. */
  ownJobId?: string;
  /**
   * bd-deepcontext-qiwz7 — expose each currently-selected blueprint's `countries`
   * to the parent so it can two-way-sync the 'Traffic from' field. Keyed by
   * promptId; the value is the blueprint's raw comma-separated `countries`
   * string (or null). Fires whenever the set of selected blueprints — or their
   * resolved country data — changes. The parent owns the union/provenance logic.
   */
  onSelectedBlueprintCountriesChange?: (
    countriesByPromptId: Record<string, string | null | undefined>,
  ) => void;
}

// Debounce delay for async search
const SEARCH_DEBOUNCE_MS = 300;

const PromptMultiSelect: React.FC<PromptMultiSelectProps> = ({
  value,
  onChange,
  placeholder = "Search blueprints...",
  disabled = false,
  required = false,
  externalPrompts,
  blueprintUsage,
  ownJobId,
  onSelectedBlueprintCountriesChange
}) => {
  const { selectedCompanyId } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt[]>([]);
  const [selectedPromptsCache, setSelectedPromptsCache] = useState<Record<string, Prompt>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchAbortController = useRef<AbortController | null>(null);
  const lastSearchKey = useRef<string | null>(null); // Track last search to prevent duplicate calls (null = never searched)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Use external prompts if provided (avoids duplicate API calls)
  useEffect(() => {
    if (externalPrompts !== undefined) {
      setPrompts(externalPrompts);
      setFilteredPrompts(externalPrompts);
      setIsLoading(false);
    }
  }, [externalPrompts]);

  // Client-side filtering when using external prompts
  useEffect(() => {
    if (externalPrompts !== undefined) {
      if (!searchQuery.trim()) {
        setFilteredPrompts(prompts);
      } else {
        const filtered = prompts.filter(prompt =>
          prompt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (prompt.description && prompt.description.toLowerCase().includes(searchQuery.toLowerCase()))
        );
        setFilteredPrompts(filtered);
      }
    }
  }, [searchQuery, prompts, externalPrompts]);

  // Server-side search when debounced search query changes (only when NOT using external prompts)
  // IMPORTANT: Check for undefined specifically - if externalPrompts is provided (even as empty array),
  // the parent is responsible for loading data and we should NOT make our own API calls
  useEffect(() => {
    if (externalPrompts === undefined) {
      // Prevent duplicate API calls with same parameters
      const searchKey = `${debouncedSearchQuery}`;
      if (searchKey !== lastSearchKey.current) {
        lastSearchKey.current = searchKey;
        searchPrompts(debouncedSearchQuery);
      }
    }
  }, [debouncedSearchQuery, externalPrompts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch details for selected prompts not in cache
  useEffect(() => {
    const missingIds = value.filter(id => !selectedPromptsCache[id] && !prompts.find(p => p.id === id));
    if (missingIds.length > 0) {
      fetchSelectedPrompts(missingIds);
    }
  }, [value, prompts]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSelectedPrompts = async (promptIds: string[]) => {
    try {
      // Fetch each missing prompt
      const fetchPromises = promptIds.map(id => (
        selectedCompanyId ? promptApi.getById(id, selectedCompanyId) : promptApi.getById(id)
      ).catch(() => null));
      const results = await Promise.all(fetchPromises);

      const newCache: Record<string, Prompt> = { ...selectedPromptsCache };
      results.forEach(prompt => {
        if (prompt) {
          newCache[prompt.id] = prompt;
        }
      });
      setSelectedPromptsCache(newCache);
    } catch (err) {
      console.error('Failed to fetch selected prompts:', err);
    }
  };

  const searchPrompts = useCallback(async (search: string) => {
    // Cancel any pending request
    if (searchAbortController.current) {
      searchAbortController.current.abort();
    }

    try {
      setIsSearching(true);
      searchAbortController.current = new AbortController();

      // Use paginated API with search for server-side filtering
      const response = await promptApi.getAllPaginated({
        page: 1,
        pageSize: 50, // Limit dropdown results
        search: search || undefined
      });

      setPrompts(response.prompts);
      setFilteredPrompts(response.prompts);
      setError(null);
      setIsLoading(false);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Failed to search prompts:', err);
        setError('Failed to load blueprints');
      }
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Get selected prompts from current list or cache
  const selectedPrompts = value
    .map(id => prompts.find(p => p.id === id) || selectedPromptsCache[id])
    .filter(Boolean) as Prompt[];

  // bd-deepcontext-qiwz7 — expose selected blueprints' country data to the
  // parent for 'Traffic from' two-way sync. Keyed by EVERY currently-selected
  // promptId so the parent can detach (key present, even if its countries
  // haven't resolved yet). The blueprint `countries` only resolve once the
  // selected-prompt cache fetches the full object, so this re-fires as data
  // arrives. Held in a ref so the parent callback need not be memoised.
  const onCountriesChangeRef = useRef(onSelectedBlueprintCountriesChange);
  onCountriesChangeRef.current = onSelectedBlueprintCountriesChange;
  useEffect(() => {
    if (!onCountriesChangeRef.current) return;
    const map: Record<string, string | null | undefined> = {};
    for (const id of value) {
      const p = prompts.find(pp => pp.id === id) || selectedPromptsCache[id];
      map[id] = p ? p.countries ?? null : undefined;
    }
    onCountriesChangeRef.current(map);
  }, [value, prompts, selectedPromptsCache]);

  // bd-n3yqb — owning job for a blueprint, if it belongs to ANOTHER scheduled
  // job. Returns null when the blueprint is free, owned by the job being
  // edited (ownJobId), or already selected on this form (so removing then
  // re-adding stays possible without flicker).
  const getBlockingOwner = useCallback(
    (promptId: string): { jobId: string; jobName: string } | null => {
      if (!blueprintUsage) return null;
      if (value.includes(promptId)) return null;
      const owner = blueprintUsage[promptId];
      if (!owner) return null;
      if (ownJobId && owner.jobId === ownJobId) return null;
      return owner;
    },
    [blueprintUsage, ownJobId, value]
  );

  const handlePromptSelect = (prompt: Prompt) => {
    // bd-n3yqb — never allow selecting a blueprint owned by another job.
    if (getBlockingOwner(prompt.id)) return;
    const isSelected = value.includes(prompt.id);
    let newValue: string[];

    if (isSelected) {
      newValue = value.filter(id => id !== prompt.id);
    } else {
      newValue = [...value, prompt.id];
      // Cache the selected prompt for display
      setSelectedPromptsCache(prev => ({ ...prev, [prompt.id]: prompt }));
    }

    onChange(newValue);
    setSearchQuery('');
    inputRef.current?.focus();
  };

  const handleRemovePrompt = (promptId: string) => {
    const newValue = value.filter(id => id !== promptId);
    onChange(newValue);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredPrompts.length > 0) {
        const firstPrompt = filteredPrompts[0];
        // bd-n3yqb — skip blueprints owned by another job.
        if (!value.includes(firstPrompt.id) && !getBlockingOwner(firstPrompt.id)) {
          handlePromptSelect(firstPrompt);
        }
      }
    }
  };

  const hasError = required && value.length === 0;

  return (
    <div className="relative" ref={dropdownRef} data-testid="prompt-multi-select">
      <div className="w-full">
        {/* Selected prompts display */}
        {selectedPrompts.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedPrompts.map((prompt) => (
              <TagPill
                key={prompt.id}
                onRemove={() => handleRemovePrompt(prompt.id)}
                removeLabel={`Remove ${prompt.name}`}
                disabled={disabled}
              >
                {prompt.name}
              </TagPill>
            ))}
          </div>
        )}

        {/* Search input */}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={selectedPrompts.length > 0 ? "Add more blueprints..." : placeholder}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent disabled:bg-[var(--ghost-bg)] disabled:cursor-not-allowed ${
              hasError ? 'border-risk focus:ring-risk' : 'border-line'
            }`}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
            {isSearching && (
              <svg className="animate-spin h-4 w-4 text-ink-muted mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <button
              type="button"
              aria-label="Toggle blueprint list"
              disabled={disabled}
              // Keep focus where it is so the click doesn't blur/refocus the input.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (isOpen) {
                  setIsOpen(false);
                  setSearchQuery('');
                } else {
                  setIsOpen(true);
                  inputRef.current?.focus();
                }
              }}
              className="flex items-center disabled:cursor-not-allowed"
            >
              <ChevronDownIcon
                className={`h-4 w-4 text-ink-muted transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* Validation error */}
        {hasError && (
          <p className="mt-1 text-xs text-risk">At least one blueprint is required</p>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-surface border border-line rounded-lg shadow-lg"
          style={{ maxHeight: '15rem', overflowY: 'auto', overscrollBehavior: 'contain' }}
          onScroll={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          {isLoading && (
            <div className="px-3 py-2 text-sm text-ink-muted">Loading blueprints...</div>
          )}

          {error && (
            <div className="px-3 py-2 text-sm text-risk">{error}</div>
          )}

          {!isLoading && !error && filteredPrompts.length === 0 && !isSearching && (
            <div className="px-3 py-2 text-sm text-ink-muted">
              {searchQuery ? 'No blueprints found' : 'No blueprints available'}
            </div>
          )}

          {!isLoading && !error && filteredPrompts.map((prompt) => {
            const isSelected = value.includes(prompt.id);
            // bd-n3yqb — blueprint owned by another scheduled job: disable it
            // and name the owner so the user understands why.
            const blockingOwner = getBlockingOwner(prompt.id);
            const isBlocked = blockingOwner !== null;
            return (
              <button
                key={prompt.id}
                type="button"
                onClick={() => handlePromptSelect(prompt)}
                disabled={isBlocked}
                aria-disabled={isBlocked}
                title={
                  isBlocked
                    ? `Already used by "${blockingOwner!.jobName}"`
                    : undefined
                }
                className={`w-full text-left px-3 py-2 text-sm ${
                  isBlocked
                    ? 'cursor-not-allowed bg-[var(--ghost-bg)] text-ink-muted'
                    : `hover:bg-[var(--ghost-bg)] ${isSelected ? 'bg-[var(--nav-active-bg)] text-accent-active' : 'text-ink'}`
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{prompt.name}</div>
                    </div>
                    {isBlocked ? (
                      <div className="text-xs text-warn mt-1 truncate">
                        Already used by "{blockingOwner!.jobName}"
                      </div>
                    ) : (
                      prompt.description && (
                        <div className="text-xs text-ink-muted mt-1 truncate">
                          {prompt.description}
                        </div>
                      )
                    )}
                  </div>
                  {isSelected && !isBlocked && (
                    <span className="text-accent-active text-xs ml-2">✓</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromptMultiSelect;